import { type Action, createAction, createSlice } from '@reduxjs/toolkit'
import clone from 'lodash/clone'
import { l } from '@manychat/manyui'

import { SELECTION_TYPE } from 'common/cms/constants/CMSSelectionType'
import * as atypes from 'common/cms/constants/cmsReduxActionTypes'
import { FSObjectType } from 'common/cms/models/FSObject/constants'
import { FSQueryDefaults } from 'common/cms/models/FSQuery/constants'
import { CmsState, FsItem } from 'common/cms/types'

interface CmsRootFolder {
  path: string
  type: FSObjectType.FOLDER
  title: ReturnType<typeof l.makeTranslate>
}

type CmsSliceState = Omit<CmsState, 'fileSystem'> & {
  fileSystem: Omit<CmsState['fileSystem'], 'byPath'> & {
    byPath: Record<string, FsItem | CmsRootFolder> | null
  }
}

const botContentFolder: CmsRootFolder = {
  path: '/',
  type: FSObjectType.FOLDER,
  title: l.makeTranslate('Automations'),
}

const initialState: CmsSliceState = {
  fileSystem: {
    byPath: {
      [botContentFolder.path]: botContentFolder,
    },
    listings: {},
  },
  flowPicker: {
    fsQuery: clone(FSQueryDefaults),
    selectedFlowId: null,
  },
  displayMode: null,
  selectionType: SELECTION_TYPE.INCLUDE,
  selectedIds: {},
  lastInteractedId: null,
  availableFilters: null,
}

export const fsObjectLoadSuccess = createAction(atypes.FS_OBJECT_LOAD_SUCCESS)
export const fsRenameObjectSuccess = createAction(atypes.FS_RENAME_OBJECT_SUCCESS)

interface FsObjectMergeAction extends Action<string> {
  item: FsItem
}

const isFsObjectMergeAction = (action: Action): action is FsObjectMergeAction =>
  action.type === fsObjectLoadSuccess.type || action.type === fsRenameObjectSuccess.type

const mergeFsObjectByPath = (state: CmsSliceState, action: FsObjectMergeAction) => {
  const { item } = action
  const byPath = state.fileSystem.byPath ?? {}
  const existing = byPath[item.path]

  if (existing) {
    Object.assign(existing, item)
  } else {
    byPath[item.path] = item
  }

  state.fileSystem.byPath = byPath
}

const cmsSlice = createSlice({
  name: 'cms',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(isFsObjectMergeAction, mergeFsObjectByPath)
  },
})

export const cmsReducer = cmsSlice.reducer
export default cmsSlice.reducer

export function updateByPathObject(
  byPath: Record<string, unknown> = {},
  paths: string[] = [],
  items: Array<{ path: string }> = [],
): Record<string, unknown> {
  const filteredByPath = Object.entries(byPath).reduce<Record<string, unknown>>(
    removePreviousPaths,
    {},
  )
  const itemsByPath = items.reduce<Record<string, unknown>>(fromEntriesByPath, {})
  return { ...filteredByPath, ...itemsByPath }

  function removePreviousPaths(
    acc: Record<string, unknown>,
    [path, value]: [string, unknown],
  ): Record<string, unknown> {
    if (!paths.includes(path)) {
      acc[path] = value
    }
    return acc
  }

  function fromEntriesByPath(
    acc: Record<string, unknown>,
    item: { path: string },
  ): Record<string, unknown> {
    acc[item.path] = item
    return acc
  }
}
